/**
 * DetailDrawerShell — 詳情 Drawer 的標準包裝。
 *
 * 4 個 Tab 的詳情 Drawer 都採用同款結構：
 *  - title: icon + mono 編號
 *  - body 頂部：橫向 status badges 區
 *  - body 下方：children（DataFieldGrid 等）
 *
 * 注意：antd 6 的 Drawer.size 自 4.17.0 起接受 number、6.2.0 起接受 string，
 * 用以替代已被棄用的 width prop。本 Shell 統一用 size 接收數字寬度，默認 560。
 */
import type { ReactNode } from 'react';
import { Drawer } from 'antd';
import { font, space, weight } from '@/styles/tokens';

interface DetailDrawerShellProps {
  /** Drawer 開關狀態 */
  open: boolean;
  /** 關閉回呼 */
  onClose: () => void;
  /** 標題前的 icon（如 <FileText size={16} ... />） */
  icon: ReactNode;
  /** 標題的 mono 文字（通常為編號） */
  monoTitle: string;
  /** 頂部狀態徽章區（StatusPill 序列等） */
  statusBadges: ReactNode;
  /** Drawer 寬度（傳入 antd Drawer 的 size prop），默認 560 */
  size?: number;
  /** Drawer body 主內容（DataFieldGrid 等） */
  children: ReactNode;
}

/**
 * 渲染標準詳情 Drawer。
 * 結構：title (icon + mono) → body (status row + children)。
 */
export function DetailDrawerShell({
  open,
  onClose,
  icon,
  monoTitle,
  statusBadges,
  size = 560,
  children,
}: DetailDrawerShellProps) {
  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: space[3] }}>
          {icon}
          <span style={{ fontFamily: font.mono, fontWeight: weight.semibold }}>{monoTitle}</span>
        </div>
      }
      open={open}
      onClose={onClose}
      size={size}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space[5] }}>
        <div style={{ display: 'flex', gap: space[2], flexWrap: 'wrap' }}>{statusBadges}</div>
        {children}
      </div>
    </Drawer>
  );
}
